import { useBoolean, useLockFn } from 'ahooks';
import { API_CODES } from '@/services/constant';

type Service<TData, TParams extends any[]> = (
  ...args: TParams
) => Promise<TData>;

export type Options<TData, TParams extends any[]> = {
  strictSuccess?: boolean;
  onBefore?: (...params: TParams) => void;
  onComplete?: (data: TData, params: TParams) => void;
  onSuccess?: (data: TData, params: TParams) => void;
  onFail?: (params: TParams, e?: TData) => void;
  onFinally?: (params: TParams) => void;
};

export function useLockAsyncFn<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options?: Options<TData, TParams>,
) {
  const [loading, { setTrue, setFalse }] = useBoolean(false);
  const {
    strictSuccess = true,
    onBefore,
    onComplete,
    onSuccess,
    onFail,
    onFinally,
  } = options || {};
  const asyncFn = useLockFn(async (...args: TParams) => {
    onBefore?.(...args);
    setTrue();
    try {
      const result = await service(...args);
      onComplete?.(result, args);
      if (strictSuccess) {
        // @ts-ignore
        if (result.errorCode === API_CODES.SUCCESS) {
          onSuccess?.(result, args);
        } else {
          onFail?.(args, result);
        }
      } else {
        onSuccess?.(result, args);
      }
      return result;
    } catch (error) {
      onFail?.(args);
    } finally {
      setFalse();
      onFinally?.(args);
    }
  });

  return {
    asyncFn,
    loading,
  };
}
